import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, DatePicker, Spin, Popconfirm } from "antd";
import {
  fetchEducations,
  addEducation,
  updateEducation,
  deleteEducation,
  handleNextStep,
  handlePrevStep,
} from "../../../features/profile/profileSlice";
import { showToastError, showToastSuccess } from "../../../utils";
import toast from "react-hot-toast";
import eduImg from "../../../assets/edit_edu.png";
import dayjs from "dayjs";

function closeModal() {
  toast.dismiss();
}

export default function EducationStep() {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const formRef = useRef(null);

  const { profileLoading, educations = [] } = useSelector(
    (state) => state.profile
  );

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    dispatch(fetchEducations());
  }, [dispatch]);

  const onFinish = async (values) => {
    const payload = {
      qualification: values.qualification,
      institutionName: values.institutionName,
      startDate: values.startDate
        ? dayjs(values.startDate).format("YYYY-MM-DD")
        : null,
      endDate: values.endDate
        ? dayjs(values.endDate).format("YYYY-MM-DD")
        : null,
    };

    const res = editingId
      ? await dispatch(updateEducation({ id: editingId, data: payload }))
      : await dispatch(addEducation(payload));

    if (res?.payload?.success) {
      showToastSuccess(
        editingId ? "Education updated successfully" : "Education added successfully",
        closeModal
      );
      form.resetFields();
      setShowForm(false);
      setEditingId(null);
      dispatch(fetchEducations());
    } else {
      showToastError(
        res?.payload?.message || "Failed to save education",
        closeModal
      );
    }
  };

  function handleEdit(item) {
    setEditingId(item.id);
    setShowForm(true);
    form.setFieldsValue({
      qualification: item.qualification,
      institutionName: item.institutionName,
      startDate: item.startDate ? dayjs(item.startDate) : null,
      endDate: item.endDate ? dayjs(item.endDate) : null,
    });
  }

  async function handleDelete(id) {
    const res = await dispatch(deleteEducation(id));
    if (res?.payload?.success) {
      showToastSuccess("Education deleted successfully", closeModal);
      dispatch(fetchEducations());
    } else {
      showToastError(
        res?.payload?.message || "Failed to delete education",
        closeModal
      );
    }
  }

  function handleGoNext() {
    dispatch(handleNextStep());
  }

  function handleGoBack() {
    dispatch(handlePrevStep());
  }

  function handleCancel() {
    form.resetFields();
    setShowForm(false);
    setEditingId(null);
  }

  if (profileLoading && !educations.length && !showForm) {
    return (
      <div className="flex items-center justify-center mt-20">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="mt-10">
      <h3 className="edit-step-head">Education</h3>
      <p className="edit-step-desc mt-2">
        List the schools you've attended below
      </p>

      <div className="flex justify-end w-full">
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="bg-transparent flex items-center border-none"
          >
            <div className="bg-darkBlue rounded-full p-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M4 16C4 15.4477 4.44772 15 5 15H27C27.5523 15 28 15.4477 28 16C28 16.5523 27.5523 17 27 17H5C4.44772 17 4 16.5523 4 16Z"
                  fill="white"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M16 4C16.5523 4 17 4.44772 17 5V27C17 27.5523 16.5523 28 16 28C15.4477 28 15 27.5523 15 27V5C15 4.44772 15.4477 4 16 4Z"
                  fill="white"
                />
              </svg>
            </div>
            <span className="text-darkBlue font-medium text-[17px] ml-3">
              Add Education
            </span>
          </button>
        )}
      </div>

      {/* Existing educations list */}
      {educations.length > 0 && !showForm && (
        <>
          {educations.map((item) => (
            <div key={item.id} className="bg-white p-6 mb-4 mt-4 rounded-lg">
              <div className="w-full flex items-start justify-between gap-4">
                <div className="flex flex-col">
                  <h3 className="text-darkBlue text-[21px] font-bold">
                    {item.qualification}
                  </h3>
                  <p className="text-black333 font-medium text-[18px] my-1">
                    {item.institutionName}
                  </p>
                  <span className="text-black333 font-normal text-[17px]">
                    {dayjs(item.startDate).format("MMM YYYY")} -{" "}
                    {dayjs(item.endDate).format("MMM YYYY")}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleEdit(item)}
                    className="text-darkBlue border border-darkBlue rounded-full px-4 py-1 text-sm font-medium hover:bg-blue-50"
                  >
                    Edit
                  </button>
                  <Popconfirm
                    title="Delete this education entry?"
                    okText="Delete"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleDelete(item.id)}
                  >
                    <button
                      type="button"
                      className="text-red-500 border border-red-500 rounded-full px-4 py-1 text-sm font-medium hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </Popconfirm>
                </div>
              </div>
            </div>
          ))}

          <div className="flex flex-col sm:flex-row items-center justify-center mt-10 mb-14 w-full">
            <button
              onClick={handleGoBack}
              className="bg-white rounded-[35px] text-darkBlue border-2 border-darkBlue w-full sm:w-[300px] p-2 mb-2 sm:mb-0 sm:mr-3"
              type="button"
            >
              Back
            </button>
            <button
              onClick={handleGoNext}
              className="bg-darkBlue rounded-[35px] text-white w-full sm:w-[300px] p-3"
              type="button"
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Empty state */}
      {!educations.length && !showForm && (
        <>
          <div className="flex items-center justify-center flex-col">
            <img
              className="object-contain w-full max-w-[550px] h-auto"
              src={eduImg}
              alt="education"
            />
            <p className="edit-step-desc my-10">
              Don't have education experience? You can skip this part
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center mt-10 mb-14 w-full">
            <button
              onClick={handleGoBack}
              className="bg-white rounded-[35px] text-darkBlue border-2 border-darkBlue w-full sm:w-[300px] p-2 mb-2 sm:mb-0 sm:mr-3"
              type="button"
            >
              Back
            </button>
            <button
              onClick={handleGoNext}
              className="bg-darkBlue rounded-[35px] text-white w-full sm:w-[300px] p-3"
              type="button"
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Add/Edit education form */}
      {showForm && (
        <Form
          layout="vertical"
          ref={formRef}
          name="add-education"
          onFinish={onFinish}
          style={{ maxWidth: "100%", marginTop: 20 }}
          form={form}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <Form.Item
              name="qualification"
              label="Qualification"
              rules={[
                { required: true, message: "Qualification is required" },
              ]}
            >
              <Input
                className="rounded-[8px] h-[44px] w-full border border-black"
                placeholder="e.g. Bachelors of Science"
              />
            </Form.Item>

            <Form.Item
              name="institutionName"
              label="Name of Institution"
              rules={[
                { required: true, message: "Institution name is required" },
              ]}
            >
              <Input
                className="rounded-[8px] h-[44px] w-full border border-black"
                placeholder="e.g. University of Nairobi"
              />
            </Form.Item>

            <Form.Item
              name="startDate"
              label="Start Date"
              rules={[{ required: true, message: "Start date is required" }]}
            >
              <DatePicker
                style={{ width: "100%", height: "44px" }}
                format="YYYY-MM-DD"
                placeholder="Select start date"
                picker="month"
              />
            </Form.Item>

            <Form.Item
              name="endDate"
              label="End Date"
              rules={[{ required: true, message: "End date is required" }]}
            >
              <DatePicker
                style={{ width: "100%", height: "44px" }}
                format="YYYY-MM-DD"
                placeholder="Select end date"
                picker="month"
              />
            </Form.Item>

          </div>

          <div className="flex flex-col sm:flex-row items-center justify-end mt-10 mb-14 w-full">
            <button
              onClick={handleCancel}
              className="bg-white rounded-[35px] text-darkBlue border-2 border-darkBlue w-full sm:w-[300px] p-2 mb-2 sm:mb-0 sm:mr-3"
              type="button"
            >
              Cancel
            </button>
            <button
              className="bg-darkBlue rounded-[35px] text-white w-full sm:w-[300px] p-3"
              type="submit"
              disabled={profileLoading}
            >
              {profileLoading ? <Spin size="small" /> : editingId ? "Update" : "Save"}
            </button>
          </div>
        </Form>
      )}
    </div>
  );
}
